import { PlusIcon } from '@src/components/icons/PlusIcon';
import { trackEvent } from '@src/utils/analytics';
import { trackUmamiEvent } from '@src/utils/umami';
import { createPortal } from 'react-dom';
import { getPortalRoot } from '@src/components/portalRoot';
import { ScrollLoadMore } from '@src/components/ScrollLoadMore/ScrollLoadMore';
import { useParticipantProgressFilter, ParticipantProgressFilterTabs } from '../ParticipantProgressFilter';
import { BulkReminderButton } from '../BulkReminder';
import { PaperPlaneIcon } from '@src/components/icons/PaperPlaneIcon';
import { OrganizerDrawContent } from './OrganizerDrawContent';
import { CogIcon } from '@src/components/icons/CogIcon';
import { UserExcludeIcon } from '@src/components/icons/UserExcludeIcon';
import { OrganizerPanelHeading } from './OrganizerPanelHeading';
import { UsersIcon } from '@src/components/icons/UsersIcon';
import { InlineOrganizerAddUsers } from './useOrganizerAddUsers';
import { useBoxRenewalError } from './useBoxRenewalError';
import { useOrganizerParticipantPage } from './useOrganizerParticipantPage';
import { questionOpener } from '@src/utils/questionAnalytics';
import { hasPremiumFeature } from '@lub0v/santa-utils';
import { useEditBoxDraw } from './useEditBoxDraw';
import { WrenchIcon } from '@src/components/icons/WrenchIcon';
import { ParticipantSearch } from '@src/components/ParticipantTable/ParticipantSearch';
import { OrganizerPanelHeader, OrganizerPanelSection } from './OrganizerPanelSection';
import { OrganizerPanelOrganizers } from '@src/pages/BoxPage/OrganizerPanelOrganizers';
import { usePersonalLinkCopied } from '@src/pages/BoxPage/usePersonalLinkCopied';
import { OrganizerPanelActivity } from '@src/pages/BoxPage/OrganizerPanelActivity';
import { ParticipantStatusColumns } from './ParticipantStatusColumns';
import { participantGifts } from './ParticipantGiftIndicators';
import { useParticipantPreview } from '@src/pages/BoxPage/ParticipantPreview';
import { useConfirmBoxDraw } from './useConfirmBoxDraw';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMobileBoxLayout } from './BoxLayouts';
import { LinkIcon } from '@src/components/icons/LinkIcon';
import { CircleDrawHelp, AfterDrawHelp } from './DrawHelp';
import { Link as InlineLink } from '@src/components/Link/Link';
import { ThemeArtwork } from '@src/theme/Artwork';
import { RefreshIcon } from '@src/components/icons/RefreshIcon';
import { QuestionIcon } from '@src/components/icons/QuestionIcon';
import { GiftIcon } from '@src/components/icons/GiftIcon';
import { Trans, useTranslation } from 'react-i18next';
import { normalizeQuickBoxName, type BoxInfo, type CardInfo } from '@lub0v/santa-utils';
import {
  Badge,
  Expandable,
  Button,
  Checkbox,
  Select,
  CloseIcon,
  Hint,
  Cluster,
  Input,
  ParticipantTable,
  ParticipantTableFill,
  ParticipantTableRowList,
  ParticipantRow,
  useParticipantTableViewport,
  Stack,
  Switch,
  Typography,
  closeDialog,
  showDialog,
  showToast,
  type MenuItem,
} from '@src/components';
import { EmailInviteForm, emailInviteError, emailInviteSendError } from '@src/pages/BoxPage/EmailInviteForm';
import { InviteMessageForm } from '@src/pages/BoxPage/InviteMessageForm';
import { SecretPair } from '@src/pages/BoxPage/SecretPair';
import {
  buildPersonalInviteMessage,
  buildSharedInviteMessage,
  composeInviteClipboard,
} from '@src/pages/BoxPage/inviteMessage';
import { useLinkInvitedIds } from '@src/pages/BoxPage/linkInviteStorage';
import { participantStatus, participantStatusTone } from '@src/pages/BoxPage/participantStatus';
import { sortOrganizerCards } from '@src/pages/BoxPage/sortCards';
import { sameOriginUrl } from '@src/routing/urls';
import { OrganizerInlineExclusions } from './OrganizerInlineExclusions';
import { useOrganizerBoxExclusions } from './useOrganizerBoxExclusions';
import { OrganizerInvitationGuide } from './OrganizerInvitationGuide';
import { boxMessengerPath } from '@src/routing/routes';
import { useParticipantSelection } from './useParticipantSelection';
import './OrganizerHome.css';
import { santaClient } from '@src/state/client';
import { pushDialog } from '@src/components/Dialog/Dialog';
import { editBoxInput } from '@src/pages/BoxPage/boxInput';

export type OrganizerSort = { column: 'card' | 'participant' | 'outgoing' | 'incoming' | 'giftee'; descending: boolean };

export interface OrganizerPanelHomeProps {
  inDialog?: boolean;
  inlineMobile?: boolean;
  sorting?: [OrganizerSort[], React.Dispatch<React.SetStateAction<OrganizerSort[]>>];
  section?: 'all' | 'users' | 'invite' | 'organizers' | 'draw';
  embedded?: boolean;
  info: BoxInfo;
  cards: CardInfo[];
  onChanged: () => Promise<void> | void;
}


export function OrganizerPanelHome({ info, cards: initialCards, onChanged, section = 'all', embedded = false, sorting, inlineMobile = false, inDialog = false }: OrganizerPanelHomeProps) {
  const mobile = useMobileBoxLayout();
  const copyDialogPresented = useRef(false);
  const [mobileCursor, setMobileCursor] = useState({ key: '', page: 1 });
  const [mobileVisibleCount, setMobileVisibleCount] = useState(20);
  const progressContext = useParticipantProgressFilter();
  const progressFilter = progressContext?.filter;
  const progressQuery = progressFilter ? Object.entries(progressContext?.criteria ?? {}).map(([status, side]) => `${status}:${side}`).join(',') : '';
  const paged = info.participantsNumber >= 100 && (section === 'users' || section === 'all');
  const [pageRetry, setPageRetry] = useState(0);
  const pageRevision = useMemo(() => ({ info, pageRetry }), [info, pageRetry]);
  const [pageRequest, setPageRequest] = useState({ page: 1, pageSize: 12, search: '', filters: '', sort: '' });
  const mobileQueryKey = JSON.stringify([info.box.key, pageRequest.search, pageRequest.filters, pageRequest.sort, progressQuery]);
  const mobilePage = mobileCursor.key === mobileQueryKey ? mobileCursor.page : 1;
  const remote = useOrganizerParticipantPage(info.box.key, paged, { ...pageRequest, ...(mobile ? { page: mobilePage, pageSize: 20 } : {}), progress: progressQuery }, pageRevision, mobile);
  const cards = paged ? remote.cachedCards : initialCards;
  const loadActionCards = async () => initialCards;
  const navigate = useNavigate();
  const location = useLocation();
  const consumedPreview = useRef<string | null>(null);
  const readyToDraw = (paged ? remote.data?.summary.named ?? 0 : cards.filter((card) => card.username).length) >= 3;
  const editParticipantExclusions = useOrganizerBoxExclusions(info, loadActionCards, onChanged);
  const { t, i18n } = useTranslation();
  const handleRenewalError = useBoxRenewalError(info);
  const openParticipantPreview = useParticipantPreview();
  const [savingDrawMode, setSavingDrawMode] = useState(false);
  async function saveDrawMode(useCircleDraw: boolean) {
    if (savingDrawMode || info.box.isArchived || info.isActiveDraw) return;
    setSavingDrawMode(true);
    try {
      const result = await santaClient.updateBox({ ...editBoxInput(info), useCircleDraw });
      if (!result.ok) {
        showToast(t(result.error?.message || 'newBox.saveFailed'));
        return;
      }
      await onChanged();
      showToast(t('game.settings.saved'), 'success');
    } catch {
      showToast(t('newBox.saveFailed'));
    } finally {
      setSavingDrawMode(false);
    }
  }


  const [emailDialog, setEmailDialog] = useState<{
    card: CardInfo;
    email: string;
    consent: boolean;
    emailError: string | null;
    error: string | null;
  } | null>(null);
  const [copyDialog, setCopyDialog] = useState<{
    card: CardInfo;
    link: string;
  } | null>(null);
  const [sharedOpen, setSharedOpen] = useState(false);
  const copyMessageRef = useRef('');
  const sharedMessageRef = useRef('');
  const [rename, setRename] = useState<{
    card: CardInfo;
    name: string;
    error: string | null;
  } | null>(null);
  const [pair, setPair] = useState<{
    giver: CardInfo;
    receiver: CardInfo;
    kind: 'giftee' | 'santa';
  } | null>(null);
  const archived = Boolean(info.box.isArchived);
  const isActiveDraw = Boolean(info.isActiveDraw);
  const sharedLink = info.inviteLink ? sameOriginUrl(info.inviteLink) : '';
  const cardIds = useMemo(() => cards.map((card) => card.id), [cards]);
  const { copiedSet, markLinkInvited } = useLinkInvitedIds(info.box.key, cardIds);
  const recordLinkCopied = usePersonalLinkCopied(info.box.key, markLinkInvited, onChanged);
  const [pageSizeChoice, setPageSizeChoice] = useState('auto');
  const localSorting = useState<OrganizerSort[]>([]);
  const [sorts, setSorts] = sorting ?? localSorting;
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const freeProgress = useOrganizerParticipantPage(info.box.key, !paged && !hasPremiumFeature(info.box, 'boxActivityLog'), { page: 1, pageSize: 100, search: '', filters: '', sort: '' }, pageRevision);
  const progressById = useMemo(() => new Map((freeProgress.data?.progressParticipants ?? progressContext?.people ?? []).map(person => [person.id, person])), [progressContext?.people, freeProgress.data]);
  const orderedCards = useMemo(() => sortOrganizerCards(cards).sort((a, b) => {
    const rank: Record<string, number> = { unknown: 0, pending: 1, sent: 2, received: 3 };
    for (const sort of sorts) {
      let comparison = 0;
      if (sort.column === 'participant') comparison = a.username.localeCompare(b.username, i18n.language, { numeric: true, sensitivity: 'base' });
      else if (sort.column === 'card') comparison = (progressById.get(a.id)?.filled ? 2 : a.isVerified ? 1 : 0) - (progressById.get(b.id)?.filled ? 2 : b.isVerified ? 1 : 0);
      else if (sort.column === 'giftee') comparison = (a.isWardKnown ? 2 : a.wardId ? 1 : 0) - (b.isWardKnown ? 2 : b.wardId ? 1 : 0);
      else comparison = rank[participantGifts(a, cards)[sort.column]] - rank[participantGifts(b, cards)[sort.column]];
      if (comparison) return comparison * (sort.descending ? -1 : 1);
    }
    return 0;
  }), [cards, sorts, i18n.language, progressById]);
  const localProgressCards = useMemo(() => {
    if (!progressFilter || paged) return orderedCards;
    return orderedCards.filter(card => {
      const person = progressById.get(card.id);
      if (!person) return false;
      return Object.entries(progressContext?.criteria ?? {}).every(([status, side]) => {
        const done = person[status as 'opened' | 'filled' | 'revealed' | 'sent' | 'received'];
        return side === 'done' ? done : !done;
      });
    });
  }, [orderedCards, progressFilter, paged, progressById, progressContext?.criteria]);
  const sorted = useMemo(() => localProgressCards.filter((card) => statusFilters.every((statusFilter) => {
    switch (statusFilter) {
      case 'unassigned': return !card.wardId;
      case 'unseen': return !card.isWardKnown;
      case 'unopened': return !card.isVerified;
      case 'incomplete': return !card.username?.trim()
        || (info.box.useWish && !(card.wishPreview?.length || card.wish?.trim()))
        || (info.box.usePost && !(card.postalAddressPreview?.length || card.postalAddress?.trim()))
        || (info.box.isPhoneRequired && !(card.phonePreview?.length || card.phone?.trim()));
      case 'unsent': {
        const outgoing = participantGifts(card, cards).outgoing;
        return outgoing !== 'sent' && outgoing !== 'received';
      }
      case 'unreceived': return !card.isGiftReceived;
      default: return true;
    }
  })), [localProgressCards, statusFilters, info.box, cards]);
  const confirmDraw = useConfirmBoxDraw(info, async () => orderedCards, onChanged);
  const editDraw = useEditBoxDraw(info, async () => orderedCards, onChanged);
  const { tableRef, ...participants } = useParticipantTableViewport({
    items: paged ? remote.data?.items ?? [] : sorted,
    remoteTotal: paged ? remote.data?.total ?? info.participantsNumber : undefined,
    alwaysUseMaxPageSize: !mobile && paged && !progressFilter && pageSizeChoice === 'auto' && (remote.data?.total ?? 0) > 0,
    willPaginate: paged ? true : undefined,
    pageSize: mobile ? 20 : pageSizeChoice === 'auto' ? undefined : Number(pageSizeChoice),
    alwaysSearch: true,
    getSearchText: (card) => card.username || '',
  });
  const visibleParticipants = mobile ? (paged ? remote.data?.items ?? [] : participants.filtered.slice(0, mobileVisibleCount)) : participants.visible;
  const mobileHasMore = mobile && visibleParticipants.length < (paged ? remote.data?.total ?? 0 : participants.filtered.length);
  // eslint-disable-next-line react-hooks/set-state-in-effect -- A changed filter resets pagination and selection before loading its results.
  useEffect(() => { setMobileVisibleCount(20); }, [participants.query, progressQuery]);
  // Keep the existing content height while filtering; do not invent a viewport-sized empty table.
  const unfilteredTableHeight = useRef(0);
  const firstPageListHeight = useRef(0);
  useLayoutEffect(() => {
    const host = tableRef.current?.closest<HTMLElement>('.participant-table-fill');
    if (!host || mobile) return;
    const list = tableRef.current?.querySelector<HTMLElement>('.participant-table__list');
    if (progressFilter || participants.page > 1) {
      if (list && firstPageListHeight.current) list.style.minHeight = `${firstPageListHeight.current}px`;
      if (unfilteredTableHeight.current) host.style.minHeight = `${unfilteredTableHeight.current}px`;
      return () => { host.style.removeProperty('min-height'); list?.style.removeProperty('min-height'); };
    }
    host.style.removeProperty('min-height');
    list?.style.removeProperty('min-height');
    const measure = () => {
      unfilteredTableHeight.current = host.getBoundingClientRect().height;
      firstPageListHeight.current = list?.getBoundingClientRect().height ?? 0;
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    return () => observer.disconnect();
  }, [Boolean(progressFilter), participants.page, pageSizeChoice, mobile, tableRef]);
  const { query } = participants;
  const requestSort = sorts.map(sort => `${sort.column}:${sort.descending ? 'desc' : 'asc'}`).join(',');
  const requestFilters = statusFilters.join(',');
  useEffect(() => {
    if (!paged) return;
    const timer = window.setTimeout(() => setPageRequest({ page: participants.page, pageSize: pageSizeChoice === 'auto' ? participants.pageSize ?? participants.maxRows : Number(pageSizeChoice),
      search: query, filters: requestFilters, sort: requestSort }), 0);
    return () => window.clearTimeout(timer);
  }, [paged, participants.page, participants.pageSize, participants.maxRows, pageSizeChoice, query, requestFilters, requestSort]);
  useEffect(() => {
    const cardId = location.state?.previewCardId;
    if (!info.isAdmin || typeof cardId !== 'number' || consumedPreview.current === location.key) return;
    if (paged) {
      consumedPreview.current = location.key;
      void fetch(`/api/box/${encodeURIComponent(info.box.key)}/participants?cardId=${cardId}&pageSize=1`)
        .then(async response => {
          if (!response.ok) throw new Error('Participant unavailable');
          const data = await response.json();
          if (!data.items[0]) throw new Error('Participant unavailable');
          openParticipantPreview({ card: data.items[0], people: [...data.items, ...data.related], box: info.box,
            index: 0, copiedIds: copiedSet, onChanged, isActiveDraw: Boolean(info.isActiveDraw) }, mobile);
          const state = { ...location.state };
          delete state.previewCardId;
          navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, { replace: true, state });
        }).catch(() => showToast(t('errors.UNKNOWN')));
      return;
    }
    const index = orderedCards.findIndex(card => card.id === cardId);
    if (index < 0) return;
    consumedPreview.current = location.key;
    if (participants.pageSize) participants.setPage(Math.floor(index / participants.pageSize) + 1);
    openParticipantPreview({ card: orderedCards[index], people: cards, box: info.box,
      index, copiedIds: copiedSet, onChanged, isActiveDraw: Boolean(info.isActiveDraw) }, mobile);
    const state = { ...location.state };
          delete state.previewCardId;
    navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, { replace: true, state });
  }, [location, info, orderedCards, cards, copiedSet, onChanged, openParticipantPreview, participants.pageSize, participants.setPage, navigate, paged, t]);

  const [compactRows, setCompactRows] = useState(() => {
    try { return localStorage.getItem('santa.organizer.compact') === 'true'; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem('santa.organizer.compact', String(compactRows)); } catch { /* Storage may be unavailable. */ } }, [compactRows]);
  const selection = useParticipantSelection(info, cards, onChanged);
  const [selectionPortal, setSelectionPortal] = useState(getPortalRoot);
  const selectionBarRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const bar = selectionBarRef.current;
    const content = mobile && !inlineMobile ? tableRef.current?.closest<HTMLElement>('.dialog__body') : tableRef.current?.closest<HTMLElement>('.participant-table-fill');
    if (!bar || !content) return;
    setSelectionPortal(mobile ? content.closest<HTMLElement>('.dialog__card') ?? getPortalRoot() : getPortalRoot());
    const previous = content.style.paddingBottom;
    const update = () => { content.style.paddingBottom = `${bar.getBoundingClientRect().height + 16}px`; };
    const position = () => {
      if (!mobile || inlineMobile) bar.style.bottom = `${Math.max(16, window.innerHeight - content.getBoundingClientRect().bottom + 16)}px`;
    };
    update();
    position();
    const observer = new ResizeObserver(() => { update(); position(); });
    observer.observe(bar);
    observer.observe(content);
    window.addEventListener('scroll', position, true);
    window.addEventListener('resize', position);
    return () => { observer.disconnect(); window.removeEventListener('scroll', position, true); window.removeEventListener('resize', position); content.style.paddingBottom = previous; };
  }, [selection.selectedIds.length > 0, mobile, inlineMobile]);
  useEffect(() => {
    participants.setPage(1);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- A changed filter resets pagination and selection before loading its results.
    setStatusFilters([]);
    selection.clear();
  }, [progressFilter]);
  useEffect(() => {
    if (!progressContext?.cardSelection || mobile) return;
    const frame = requestAnimationFrame(() => {
      const table = tableRef.current;
      const panel = table?.closest<HTMLElement>('.organizer-panel-section, .panel');
      (panel ?? table?.closest<HTMLElement>('.game-participants'))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, [progressContext?.cardSelection, mobile]);

  const [searchExpanded, setSearchExpanded] = useState(false);
  const [addingUsers, setAddingUsers] = useState(false);
  const canManage = info.isAdmin && !archived;
  const unassignedCount = isActiveDraw ? (paged ? remote.data?.summary.unassigned ?? 0 : cards.filter((card) => !card.wardId).length) : 0;

  useEffect(() => {
    if (!emailDialog) {
      return;
    }
    showDialog({
      title: t('box.organizer.email.title', { name: emailDialog.card.username }),
      children: (
        <EmailInviteForm
          intro={t('box.organizer.email.body')}
          email={emailDialog.email}
          onEmailChange={(email) =>
            setEmailDialog((current) =>
              current ? { ...current, email, emailError: null, error: null } : current,
            )
          }
          onEmailBlur={() =>
            setEmailDialog((current) =>
              current
                ? { ...current, emailError: emailInviteError(current.email, t) ?? null }
                : current,
            )
          }
          emailLabel={t('box.organizer.email.label')}
          hint={t('box.organizer.email.hint')}
          consentGiven={emailDialog.consent}
          onConsentChange={(consent) =>
            setEmailDialog((current) => (current ? { ...current, consent } : current))
          }
          emailError={emailDialog.emailError}
          error={emailDialog.error}
          testId="game-email-form"
        />
      ),
      cancel: t('box.cancel'),
      submit: t('box.organizer.email.submit'),
      submitDisabled: !emailDialog.consent || Boolean(emailInviteError(emailDialog.email, t)),
      onSubmit: async () => {
        const email = emailDialog.email.trim();
        const invalid = emailInviteError(email, t);
        if (invalid) {
          setEmailDialog((current) => (current ? { ...current, emailError: invalid } : current));
          throw new Error('invalid email');
        }
        const res = await santaClient.sendCardLink({
          key: info.box.key,
          id: emailDialog.card.id,
          email,
        });
        if (!res.ok) {
          setEmailDialog((current) =>
            current
              ? { ...current, error: emailInviteSendError(res.error, t) }
              : current,
          );
          throw new Error('email failed');
        }
        showToast(t('box.organizer.emailSent', { name: emailDialog.card.username }), 'success');
        setEmailDialog(null);
      },
      onCancel: () => setEmailDialog(null),
      testId: 'game-email-dialog',
    });
  }, [emailDialog, info.box.key, t]);

  useEffect(() => {
    if (!copyDialog) {
      return;
    }
    // Keep the table portal mounted while its asynchronous link request completes.
    const present = inDialog && !copyDialogPresented.current ? pushDialog : showDialog;
    copyDialogPresented.current = true;
    present({
      title: t('box.organizer.copyLink.title'),
      mobileFullScreen: true,
      children: (
        <InviteMessageForm
          message={copyMessageRef.current}
          onMessageChange={(message) => {
            copyMessageRef.current = message;
          }}
          messageLabel={t('box.invite.messageLabel')}
          messageHint={t('box.invite.linkPlaceholderHint')}
          link={copyDialog.link}
          onLinkCopied={() => { void recordLinkCopied(copyDialog.card.id); }}
          linkLabel={t('box.organizer.copyLink.linkLabel')}
          warning={t('box.organizer.copyLink.warning')}
          testId="game-copy-form"
        />
      ),
      cancel: t('box.cancel'),
      submit: t('box.organizer.copyLink.submit'),
      submitDisabled: !copyDialog.link,
      onSubmit: async () => {
        const text = composeInviteClipboard(copyMessageRef.current, copyDialog.link, [
          t('box.invite.personal.openLink'),
          t('box.invite.shared.openLink'),
        ]);
        try {
          await navigator.clipboard.writeText(text);
        } catch {
          showToast(t('box.organizer.copyFailed'));
          throw new Error('copy failed');
        }
        void recordLinkCopied(copyDialog.card.id);
        showToast(t('box.organizer.messageCopied'), 'success');
        setCopyDialog(null);
      },
      onCancel: () => setCopyDialog(null),
      testId: 'game-copy-dialog',
    });
  }, [copyDialog, t, recordLinkCopied, inDialog]);

  useEffect(() => {
    if (!sharedOpen) {
      return;
    }
    showDialog({
      title: t('box.organizer.shared.title'),
      mobileFullScreen: true,
      children: (
        <InviteMessageForm
          message={sharedMessageRef.current}
          onMessageChange={(message) => {
            sharedMessageRef.current = message;
          }}
          messageLabel={t('box.invite.messageLabel')}
          messageHint={t('box.invite.linkPlaceholderHint')}
          link={sharedLink}
          linkLabel={t('box.organizer.shared.linkLabel')}
          warning={t('box.organizer.shared.warning')}
          testId="game-shared-form"
        />
      ),
      cancel: t('box.cancel'),
      submit: t('box.organizer.shared.submit'),
      onSubmit: async () => {
        const text = composeInviteClipboard(sharedMessageRef.current, sharedLink, [
          t('box.invite.shared.openLink'),
          t('box.invite.personal.openLink'),
        ]);
        try {
          await navigator.clipboard.writeText(text);
        } catch {
          showToast(t('box.organizer.copyFailed'));
          throw new Error('copy failed');
        }
        showToast(t('box.organizer.messageCopied'), 'success');
        setSharedOpen(false);
      },
      onCancel: () => setSharedOpen(false),
      testId: 'game-shared-dialog',
    });
  }, [sharedOpen, sharedLink, t]);

  useEffect(() => {
    if (!rename) {
      return;
    }
    showDialog({
      title: t('box.organizer.renameParticipant.title'),
      children: (
        <Input
          value={rename.name}
          onValueChange={(name) =>
            setRename((current) => (current ? { ...current, name, error: null } : current))
          }
          label={t('box.organizer.renameParticipant.label')}
          error={rename.error ?? undefined}
          testId="game-rename-input"
        />
      ),
      cancel: t('box.cancel'),
      submit: t('box.save'),
      onSubmit: async () => {
        const username = rename.name.trim();
        if (username === rename.card.username) {
          setRename(null);
          return;
        }
        const taken = cards.some(
          (card) =>
            card.id !== rename.card.id &&
            normalizeQuickBoxName(card.username) === normalizeQuickBoxName(username),
        );
        if (taken) {
          setRename((current) =>
            current ? { ...current, error: t('randomizer.problem.duplicate') } : current,
          );
          throw new Error('duplicate');
        }
        const res = await santaClient.updateOtherCard({
          key: info.box.key,
          id: rename.card.id,
          username,
        });
        if (!res.ok) {
          setRename((current) =>
            current ? { ...current, error: t(res.error?.message || 'newBox.saveFailed') } : current,
          );
          throw new Error('rename failed');
        }
        setRename(null);
        await onChanged();
      },
      onCancel: () => setRename(null),
    });
  }, [cards, info.box.key, onChanged, rename, t]);

  useEffect(() => {
    if (!pair) {
      return;
    }
    const subject = pair.kind === 'santa' ? pair.receiver : pair.giver;
    showDialog({
      title: t('box.organizer.giftee.pairTitle'),
      children: (
        <SecretPair
          santa={pair.giver}
          giftee={pair.receiver}
          question={t(
            pair.kind === 'santa'
              ? 'box.organizer.giftee.confirmSanta'
              : 'box.organizer.giftee.confirmGiftee',
            { name: subject.username },
          )}
          revealLabel={t('box.organizer.giftee.confirmYes')}
          santaLabel={t('box.organizer.giftee.santa')}
          gifteeLabel={t('box.organizer.giftee.giftee')}
          testId="game-pair"
        />
      ),
      onCancel: () => setPair(null),
      testId: 'game-pair-dialog',
    });
  }, [pair, t]);

  function rowMenu(card: CardInfo): MenuItem[] {
    if (!info.isAdmin || archived) {
      return [];
    }
    const items: MenuItem[] = [];
    if (!info.isActiveDraw) {
      items.push({
        id: 'exclusions',
        label: t('randomizer.exclusions.configure'),
        onSelect: () => editParticipantExclusions(card),
      });
    }
    const giftee = cards.find((person) => person.id === card.wardId);
    const santa = cards.find((person) => person.wardId === card.id);
    if (info.isActiveDraw && giftee) {
      items.push({
        id: 'giftee',
        label: t('box.organizer.menu.showGiftee'),
        onSelect: () => setPair({ giver: card, receiver: giftee, kind: 'giftee' }),
      });
    }
    if (info.isActiveDraw && santa) {
      items.push({
        id: 'santa',
        label: t('box.organizer.menu.showSanta'),
        onSelect: () => setPair({ giver: santa, receiver: card, kind: 'santa' }),
      });
    }
    items.push({
      id: 'delete',
      label: t('game.people.remove'),
      danger: true,
      onSelect: () => {
        showDialog({
          title: t('game.people.removeTitle'),
          children: t(((card.isYou && info.isAdmin) || info.organizers?.some((person) => person.id === card.id)) ? 'game.people.removeOrganizerBody' : 'game.people.removeBody', { name: card.username }) + (info.isActiveDraw && card.wardId ? ' ' + t('game.people.removeAfterDrawHint') : ''),
          cancel: t('box.cancel'),
          submit: t('game.people.remove'),
          submitVariant: 'danger',
          onSubmit: async () => {
            const res = await santaClient.deleteOtherCard({
              key: info.box.key,
              id: card.id,
            });
            if (!res.ok) {
              showToast(t(res.error?.message || 'game.people.addFailed'));
              throw new Error('delete failed');
            }
            await onChanged();
          },
        });
      },
    });
    return items;
  }

  function confirmReset() {
    showDialog({
      title: t('game.draw.resetTitle'),
      children: t('game.draw.resetBody'),
      cancel: t('box.cancel'),
      submit: t('game.draw.reset'),
      submitVariant: 'danger',
      onSubmit: async () => {
        const res = await santaClient.resetDraw({ key: info.box.key });
        if (!res.ok) {
          if (!handleRenewalError(res.error?.message)) showToast(t(res.error?.message || 'game.draw.failed'));
          throw new Error('reset failed');
        }
        await onChanged();
      },
    });
  }

  function openShared() {
    if (!sharedLink) {
      return;
    }
    sharedMessageRef.current = buildSharedInviteMessage(
      { boxName: info.box.name, isActiveDraw },
      t,
    );
    setSharedOpen(true);
  }

  async function openCopyDialog(card: CardInfo) {
    copyDialogPresented.current = false;
    copyMessageRef.current = buildPersonalInviteMessage(
      { username: card.username, boxName: info.box.name, isActiveDraw },
      t,
    );
    setCopyDialog({
      card,
      link: '',
    });
    const result = await santaClient.createCardAccessLink({
      key: info.box.key,
      id: card.id,
    });
    if (!result.ok || !result.data?.url) {
      setCopyDialog(null);
      closeDialog();
      showToast(t('box.organizer.linkFailed'));
      return;
    }
    const url = sameOriginUrl(result.data.url);
    setCopyDialog((current) =>
      current?.card.id === card.id ? { ...current, link: url } : current,
    );
  }

  function personCells(card: CardInfo) {
    const status = card.isVerified ? 'card_opened' : participantStatus(card, copiedSet);
    const menu = rowMenu(card);
    const canCopy = !card.isYou && !card.isVerified && !archived;
    const canEmail = !card.isYou && !archived;

    return {
      status,
      menu,
      canCopy,
      canEmail,
    };
  }

  const reminderCriteria = progressContext?.criteria ?? {};
  const reminderPrefill = Object.keys(reminderCriteria).length === 1
    ? reminderCriteria.filled === 'pending' ? 'filled' : reminderCriteria.sent === 'pending' ? 'sent' : undefined
    : undefined;
  const selectionControls =
    canManage && selection.selectedIds.length > 0 ? (
      <div ref={selectionBarRef} className={`game-selection-bar game-selection-bar--floating${mobile && !inlineMobile ? ' game-selection-bar--sheet' : ''}`} data-testid="game-selection-bar">
        <Cluster justify="between" gap={3} align="center">
          <Cluster justify="between" align="center" gap={2} className="game-selection-bar__summary">
            <Typography as="p" role="body/sm" aria-live="polite">
              {t('game.people.selectedCount', { count: selection.selectedIds.length })}
            </Typography>
            <Button
              variant="ghost"
              size="small"
              icon={<CloseIcon />}
              aria-label={t('game.people.clearSelection')}
              onClick={selection.clear}
            />
          </Cluster>
          <Cluster gap={2} className="game-selection-bar__actions">
            <BulkReminderButton prefill={reminderPrefill} size={mobile ? 'medium' : 'large'} box={info.box} recipients={cards.filter(card => selection.selectedIds.includes(card.id) && !card.isYou)} onSent={ids => selection.toggleMany(ids, false)} />
            <Button
              variant="danger"
              size="small"
              onClick={selection.remove}
              testId="game-delete-selected"
            >
              {t('game.people.removeSelected')}
            </Button>
          </Cluster>
        </Cluster>
      </div>
    ) : undefined;

  const participantIds = new Set(cards.map(card => card.id));
  const exclusionsCount = paged ? remote.data?.summary.exclusions ?? 0 : cards.filter(card => card.exclusions?.some(id => id !== card.id && participantIds.has(id))).length;
  const exclusionsSummary = info.isAdmin && <section className="organizer-draw__card" data-testid="game-draw-exclusions-summary">
    <OrganizerPanelHeading icon={<UserExcludeIcon />} actions={<Typography role="meta/helper">{t(exclusionsCount ? 'game.draw.exclusionsConfigured' : 'game.draw.exclusionsEmpty', { count: exclusionsCount })}</Typography>}>
      {t('game.draw.exclusionsLabel')}
    </OrganizerPanelHeading>
    <OrganizerInlineExclusions info={info} cards={initialCards} onChanged={onChanged} />
  </section>;

  const drawReadiness = <Hint tone={archived ? 'info' : readyToDraw ? 'success' : 'warning'} testId="game-draw-readiness">
                <Stack gap={1}>
                  <Typography role="body/strong" color="inherit">{t(archived ? 'game.draw.archivedTitle' : readyToDraw ? 'game.draw.readyTitle' : 'game.draw.needPeopleTitle')}</Typography>
                  <Typography role="meta/helper" color="inherit">{t(archived ? 'game.draw.archivedHint' : readyToDraw ? 'organizerPanel.readyHint' : 'game.draw.needPeople')}</Typography>
                </Stack>
              </Hint>;

  const selectAll = canManage && (<Checkbox
                      fit
                      checked={visibleParticipants.length > 0 && visibleParticipants.every((card) => selection.selectedIds.includes(card.id))}
                      disabled={!visibleParticipants.length}
                      onCheckedChange={(checked) => selection.toggleMany(visibleParticipants.map((card) => card.id), checked)}
                      aria-label={t('box.organizer.filters.selectPage')}
                      label={mobile ? `${i18n.language.startsWith('ru') ? 'Все' : 'All'} (${visibleParticipants.length})` : undefined}
                      testId="game-select-page"
                    />);
  const participantSearch = <ParticipantSearch alwaysOpen={!mobile} onExpandedChange={setSearchExpanded}
                    query={query}
                    onQueryChange={participants.setSearch}
                    selected={statusFilters}
                    onSelectedChange={(values) => { setStatusFilters(values); participants.setPage(1); }}
                    placeholder={t('box.organizer.searchPlaceholder')}
                    filtersLabel={t('box.organizer.filters.label')}
                    closeLabel={t('box.close')}
                    removeLabel={(label) => t('box.organizer.filters.remove', { label })}
                    options={[]}
                  />;
  const addUsersAction = canManage && <button type="button" className="participant-table-progress-filter__toggle" data-testid="organizer-add-user-preview" aria-expanded={addingUsers} onClick={() => { if (!addingUsers) progressContext?.setExpanded(false); setAddingUsers(value => !value); }}><span aria-hidden="true">+ </span>{t('organizerPanel.addUser')}</button>;

  const panelHeader = embedded && !mobile && (section === 'users' || section === 'invite');
  return (
    <>
    {selectionControls && createPortal(selectionControls, selectionPortal)}
    {panelHeader && <OrganizerPanelHeader>
      <OrganizerPanelHeading icon={section === 'users' ? <UsersIcon /> : <PaperPlaneIcon />} >
        {t(section === 'users' ? 'box.organizer.filters.title' : 'organizerPanel.inviteUsers')}
      </OrganizerPanelHeading>
    </OrganizerPanelHeader>}
    <Stack gap={5} testId="game-organizer" className={panelHeader ? 'organizer-section__body' : undefined}>
      {(section === 'all' || section === 'invite') && <>
      {!archived ? (
        <OrganizerPanelSection gameKey={info.box.key}
          mobile={mobile}
          title={t('game.invitationGuide.title')}
          icon={<LinkIcon />}
          id="invite"
          embedded={embedded}
          defaultOpen={paged ? Boolean(remote.data && !remote.data.summary.invited && !copiedSet.size) : !cards.some(card => !card.isYou && participantStatus(card, copiedSet) !== 'not_sent')}
        >
          <Stack gap={5}>
          <OrganizerInvitationGuide hideHeading={panelHeader} role={info.isAdmin ? 'organizer' : 'participant'} key={info.box.key} onShared={sharedLink ? openShared : undefined} />
          </Stack>
        </OrganizerPanelSection>
      ) : null}
      </>}
      {(section === 'all' || section === 'invite' || section === 'organizers') && <section className="organizer-panel__invite-organizers">
        <OrganizerPanelHeading icon={<UsersIcon />}>{t('organizerPanel.organizers')}</OrganizerPanelHeading>
        <OrganizerPanelOrganizers info={info} cards={cards} loadCards={loadActionCards} onChanged={onChanged} embedded={embedded} />
      </section>}
      {section === 'all' && <OrganizerPanelActivity key={info.box.key} info={info} cards={cards} />}
      {(section === 'all' || section === 'draw') && <>
      <OrganizerPanelSection gameKey={info.box.key}
        mobile={mobile}
        title={<span className="game-draw-heading">{t('game.draw.sectionTitle')}{unassignedCount > 0 && !archived ? <Badge tone="warning" className="game-draw-heading__warning" testId="game-draw-needs-attention">{t('game.draw.needsAttention')}</Badge> : isActiveDraw && <Badge tone="success" className="game-draw-heading__done" testId="game-draw-completed">✓ {t('game.draw.completedLabel')}</Badge>}</span>}
        icon={<GiftIcon />}
        id="draw"
        hideHeading={mobile}
        defaultOpen={section === 'draw' || (!archived && (unassignedCount > 0 || (readyToDraw && !isActiveDraw)))}
      >
        <Stack gap={4} className="game-draw-panel" testId="game-draw-section">
          <OrganizerDrawContent completed={isActiveDraw}
            draw={isActiveDraw ? <Stack gap={5}>
            {unassignedCount > 0 && !archived && <Hint tone="error" testId="game-unassigned-warning">{t('game.people.unassignedWarning')}</Hint>}
            <div className="game-draw-panel__layout game-draw-complete" data-testid="game-draw-complete">
              <div className="game-draw-complete__message">
                <ThemeArtwork role="gift" className="game-draw-complete__gift" />
                <Stack gap={3} className="game-draw-complete__copy">
                  <Typography as="p" role="body/strong">{t('game.draw.completed.title')}</Typography>
                  <Typography as="p" role="body/base">{t('game.draw.completed.body')}</Typography>
                  <Typography as="p" role="body/base">{t(hasPremiumFeature(info.box, 'boxDraw') ? 'game.draw.completed.premiumHint' : 'game.draw.completed.hint')}</Typography>
                </Stack>
              </div>
              {!archived && <div className="game-draw-panel__action game-draw-complete__actions">
                <Button variant="secondary" fullWidth icon={<RefreshIcon />} onClick={confirmReset} testId="game-reset-draw">{t('game.draw.reset')}</Button>
                <Button variant={unassignedCount > 0 ? "primary" : "secondary"} fullWidth icon={<WrenchIcon />} onClick={editDraw} testId="game-edit-draw" className="game-draw-complete__edit">{t(unassignedCount > 0 ? 'game.draw.fixDraw' : 'game.draw.completed.viewOrEdit')}</Button>
              </div>}
            </div>
          </Stack> :             <Stack gap={4} className="organizer-draw__ready">
              {archived && drawReadiness}
            {!archived ? (
              <div className="game-draw-panel__action organizer-draw__action">
                {!isActiveDraw && readyToDraw && <div className="game-draw-panel__celebration" aria-hidden="true">
                  <ThemeArtwork role="gift" className="game-draw-panel__gift" />
                  <span>{t('game.draw.magicTime')}</span>
                </div>}
              {drawReadiness}
              <Button
                variant={isActiveDraw ? 'secondary' : 'primary'}
                icon={isActiveDraw ? <RefreshIcon /> : <GiftIcon />}
                size="large"
                disabled={savingDrawMode || (!isActiveDraw && !readyToDraw)}
                onClick={isActiveDraw ? confirmReset : confirmDraw}
                testId={isActiveDraw ? 'game-reset-draw' : 'game-conduct-draw'}
              >
                {t(isActiveDraw ? 'game.draw.reset' : 'game.draw.submit')}
              </Button>
                <Cluster gap={2} justify="center">
                  <InlineLink variant="inline" testId="game-after-help" onClick={questionOpener('draw_after', info.isAdmin ? 'organizer' : 'participant', () => showDialog({
                    title: t('game.draw.afterHelp.title'), children: <AfterDrawHelp />,
                    submit: t('game.draw.circleHelp.close'), testId: 'after-draw-help',
                  }))}><QuestionIcon /><span>{t('game.draw.afterHelp.title')}</span></InlineLink>
                </Cluster>
              </div>
            ) : null}
            </Stack>}
            settings={<Stack gap={4} className={!mobile ? "organizer-draw__card" : undefined}>
              <Stack gap={4} className={mobile ? "organizer-draw__card" : undefined}>
                <OrganizerPanelHeading icon={<CogIcon />} actions={<Switch
                  checked={Boolean(info.box.useCircleDraw)}
                  disabled={archived || isActiveDraw || savingDrawMode}
                  aria-label={t('game.settings.circle')}
                  aria-describedby="organizer-circle-description"
                  onCheckedChange={(useCircleDraw) => void saveDrawMode(useCircleDraw)}
                  testId="game-circle-draw"
                />}>
                  {t('game.settings.circle')}
                </OrganizerPanelHeading>
                <Hint tone="info"><Typography as="p" role="body/sm" id="organizer-circle-description"><Trans i18nKey={info.box.useCircleDraw ? 'organizerPanel.circleEnabledHint' : 'organizerPanel.circleDisabledHint'} components={{ strong: <strong /> }} />{isActiveDraw && <> {t('organizerPanel.circleLockedAfterDraw')}</>}</Typography></Hint>

              </Stack>
              {mobile ? <Stack gap={3}>
                <Typography as="h3" role="body/strong">{t('organizerPanel.circleQuestion')}</Typography>
                <CircleDrawHelp />
              </Stack> : <Expandable elevated={false} title={t('organizerPanel.circleQuestion')} testId="organizer-circle-help">
                <CircleDrawHelp />
              </Expandable>}
            </Stack>}
            exclusions={mobile && info.isAdmin ? <Stack gap={4}>
              <OrganizerPanelHeading icon={<UserExcludeIcon />}>{t('gameExclusions.title')}</OrganizerPanelHeading>
              <OrganizerInlineExclusions info={info} cards={initialCards} onChanged={onChanged} />
            </Stack> : exclusionsSummary}
          />

        </Stack>
      </OrganizerPanelSection>
      </>}
      {(section === 'all' || section === 'users') && <div className={`game-participants${compactRows ? ' game-participants--compact' : ''}${mobile ? ' game-participants--mobile' : ''}`} aria-busy={paged && remote.loading}>
        <Stack gap={4}>
          {mobile && inlineMobile && <OrganizerPanelHeading icon={<UsersIcon />}>{t('box.organizer.filters.title')} ({info.participantsNumber})</OrganizerPanelHeading>}
          <ParticipantProgressFilterTabs actions={mobile ? <div className="game-participants__mobile-tools" data-search-expanded={searchExpanded}>{participantSearch}{canManage && !searchExpanded && <Button variant="ghost" icon={<PlusIcon />} aria-label={t('organizerPanel.addUser')} testId="organizer-add-user-preview" aria-expanded={addingUsers} onClick={() => { progressContext?.setExpanded(false); setAddingUsers(value => !value); }} />}</div> : addUsersAction} search={!mobile && participantSearch} />
          {canManage && <div className="game-participants__add-users" hidden={!addingUsers}><InlineOrganizerAddUsers info={info} loadCards={loadActionCards} onChanged={onChanged} onDone={() => setAddingUsers(false)} /></div>}
          {paged && remote.error && <Hint tone="error" testId="game-participants-error">
            {t('errors.UNKNOWN')} <Button variant="secondary" size="small" onClick={() => setPageRetry(value => value + 1)}>{t('premiumBuy.offerRetry')}</Button>
          </Hint>}
          <ParticipantTableFill viewportPage>
            <ParticipantTable
              embedded={embedded}
              ref={tableRef}
              compose={!mobile && !panelHeader && <OrganizerPanelHeading icon={<UsersIcon />}>
                {t('box.organizer.filters.title')}
              </OrganizerPanelHeading>}
              testId="game-participants"
              composeDivider={false}
              compactSearch
              rangeBelowToolbar
              paginationAtTop
              rangeTrailing={<Checkbox fit checked={compactRows} onCheckedChange={enabled => {
                  setCompactRows(enabled);
                  const data = { enabled, layout: mobile ? 'mobile' : 'desktop' };
                  try { trackEvent('participant_compact_changed', data); } catch { /* Analytics must not interrupt selection. */ }
                  try { void Promise.resolve(trackUmamiEvent('participant_compact_changed', data)).catch(() => {}); } catch { /* Analytics is optional. */ }
                }} label={i18n.language.startsWith('ru') ? 'Компактно' : 'Compact'} testId="participants-compact" />}
              rangeControls={mobile ? selectAll :
                <Select
                  className="game-participants__page-size"
                  inline
                  selectedLabel={t('box.organizer.filters.show', { count: pageSizeChoice === 'auto' ? t('box.organizer.filters.auto') : pageSizeChoice })}
                  value={pageSizeChoice}
                  onValueChange={(value) => { setPageSizeChoice(value); participants.setPage(1); }}
                  aria-label={t('box.organizer.filters.pageSize')}
                  options={['auto', '10', '25', '50', '100'].map((value) => ({ value, label: value === 'auto' ? t('box.organizer.filters.auto') : value }))}
                  testId="game-participants-page-size"
                />
              }
              showSearch={participants.showSearch}
              isEmpty={participants.isEmpty}
              empty={
                <Typography as="p" role="body/base">
                  {t(paged && remote.loading ? 'game.people.loadingParticipants' : query.trim() || statusFilters.length > 0 || Boolean(progressFilter) ? 'box.organizer.noResults' : 'game.people.empty')}
                </Typography>
              }
              actions={mobileHasMore && !remote.error && <ScrollLoadMore testId="participants-load-more" loading={paged && (remote.loading || remote.data?.page !== mobilePage)} onLoad={() => {
                if (paged) setMobileCursor({ key: mobileQueryKey, page: mobilePage + 1 });
                else setMobileVisibleCount(count => count + 20);
              }} />}
              pagination={{
                page: mobile ? 1 : participants.page,
                pageCount: mobile ? 1 : participants.pageCount,
                onChange: participants.setPage,
                total: paged ? remote.data?.total ?? 0 : participants.filtered.length,
                pageSize: mobile ? visibleParticipants.length : participants.pageSize ?? participants.filtered.length,
                previousLabel: t('box.organizer.pages.previous'),
                nextLabel: t('box.organizer.pages.next'),
                pageLabel: (page) => t('box.organizer.pages.page', { page }),
                testId: 'game-participants-pages',
              }}
            >
              <ParticipantTableRowList
                columns={!mobile ? [
                  ...(['participant', 'card', 'outgoing', 'incoming', 'giftee'] as const).map((column) => ({
                    label: column === 'card' ? (i18n.language.startsWith('ru') ? 'Карточка' : 'Card') : t(`box.organizer.table.${column}`),
                    leading: column === 'participant' ? <span className="participant-data-row__number-select"><span aria-hidden="true" />{selectAll}</span> : undefined,
                    sortDirection: sorts.some((sort) => sort.column === column) ? (sorts.find((sort) => sort.column === column)!.descending ? 'descending' as const : 'ascending' as const) : undefined,
                    sortPriority: sorts.findIndex((sort) => sort.column === column) + 1 || undefined,
                    onSort: () => {
                      setSorts((current) => {
                        const existing = current.find((sort) => sort.column === column);
                        if (!existing) return [...current, { column, descending: false }];
                        return existing.descending ? current.filter((sort) => sort.column !== column) : current.map((sort) => sort.column === column ? { ...sort, descending: true } : sort);
                      });
                      participants.setPage(1);
                    },
                  })),
                  { label: t('game.people.columnActions') },
                ] : undefined}
                placeholderCount={mobile || pageSizeChoice !== 'auto' ? 0 : participants.placeholderCount}
              >
                {visibleParticipants.map((card, index) => {
                  const cells = personCells(card);
                  const ordinal = mobile ? index : (participants.page - 1) * (participants.pageSize ?? 0) + index;
                  return (
                    <ParticipantRow
                      key={card.id}
                      card={card}
                      tableRow={!mobile}
                      showOrdinal
                      selection={
                        canManage ? (
                          <Checkbox
                            fit
                            checked={selection.selectedIds.includes(card.id)}
                            onCheckedChange={(checked) => selection.toggleMany([card.id], checked)}
                            aria-label={t('game.people.selectPerson', { name: card.username })}
                            testId={`game-select-${card.id}`}
                          />
                        ) : undefined
                      }
                      index={ordinal}
                      ordinal={ordinal + 1}
                      status={undefined}
                      statusTone={participantStatusTone(cells.status)}
                      statusDisplay="text"
                      statusColumns={!mobile ? <ParticipantStatusColumns card={card} people={cards} tableCells cardFilled={Boolean(progressById.get(card.id)?.filled)} /> : undefined}
                      description={
                        <>

                          {mobile && <ParticipantStatusColumns card={card} people={cards} cardFilled={Boolean(progressById.get(card.id)?.filled)} />}
                        </>
                      }
                      youLabel={card.isYou ? t('box.organizer.thatsYou') : undefined}
                      locked={archived}
                      previewLabel={t('box.organizer.preview.title', { name: card.username })}
                      previewMenuLabel={t('box.organizer.menu.viewCard')}
                      onPreview={
                        info.isAdmin
                          ? () =>
                              openParticipantPreview({ onChanged, isActiveDraw: Boolean(info.isActiveDraw),
                                card,
                                people: cards,
                                box: info.box,
                                index: ordinal,
                                copiedIds: copiedSet,
                              }, mobile)
                          : undefined
                      }
                      onRename={card.isYou || card.canOrganizerEditIdentity ? () => setRename({ card, name: card.username || '', error: null }) : undefined}
                      onCopy={cells.canCopy ? () => void openCopyDialog(card) : undefined}
                      copyLabel={t('box.organizer.menu.copy')}
                      onEmail={
                        cells.canEmail
                          ? () =>
                              setEmailDialog({
                                card,
                                email: '',
                                consent: false,
                                emailError: null,
                                error: null,
                              })
                          : undefined
                      }
                      emailLabel={t('box.organizer.menu.email')}
                      onMessage={
                        !archived && !card.isYou
                          ? () => navigate(boxMessengerPath(info.box.key, `user-${card.id}`))
                          : undefined
                      }
                      messageLabel={t('game.people.message', {
                        name: card.username || t('game.people.hidden'),
                      })}
                      menu={cells.menu}
                      menuLabel={t('box.organizer.menu.more')}
                      testId={`game-person-${card.id}`}
                    />
                  );
                })}
              </ParticipantTableRowList>
            </ParticipantTable>
          </ParticipantTableFill>
        </Stack>
      </div>}
    </Stack>
    </>
  );
}
